import React, { useState } from 'react';
import { T } from '../../lib/tokens';

// Status tag styling
const STATUS_TAGS = {
  'AT RISK':   { bg: 'rgba(224,88,88,0.10)', color: T.red, border: 'rgba(224,88,88,0.20)' },
  'DRIFTING':  { bg: 'rgba(212,146,30,0.10)', color: T.amber, border: 'rgba(212,146,30,0.20)' },
  'ON TRACK':  { bg: 'rgba(90,158,120,0.10)', color: T.green, border: 'rgba(90,158,120,0.20)' },
  'BLOCKED':   { bg: 'rgba(224,88,88,0.10)', color: T.red, border: 'rgba(224,88,88,0.20)' },
  'DONE':      { bg: 'rgba(90,158,120,0.10)', color: T.green, border: 'rgba(90,158,120,0.20)' },
  'OVERDUE':   { bg: 'rgba(224,88,88,0.10)', color: T.red, border: 'rgba(224,88,88,0.20)' },
  'PREP':      { bg: 'rgba(139,114,200,0.10)', color: T.violet, border: 'rgba(139,114,200,0.20)' },
};

const renderStatusTags = (text) => {
  const tagPattern = /\b(AT RISK|DRIFTING|ON TRACK|BLOCKED|DONE|OVERDUE|PREP)\b/g;
  const parts = [];
  let last = 0, m;
  while ((m = tagPattern.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const s = STATUS_TAGS[m[1]];
    parts.push(
      <span key={m.index} style={{
        display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
        padding: '2px 7px', borderRadius: 4, background: s.bg, color: s.color,
        border: `1px solid ${s.border}`, verticalAlign: 'middle', marginLeft: 4,
      }}>{m[1]}</span>
    );
    last = m.index + m[0].length;
  }
  if (last === 0) return null;
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const parseInline = (text) => {
  if (!text) return text;
  const parts = [];
  const re = /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|~~(.+?)~~|`(.+?)`|\[(.+?)\]\((.+?)\))/g;
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[2]) parts.push(<strong key={m.index} style={{ fontWeight: 700, color: T.text, fontStyle: 'italic' }}>{m[2]}</strong>);
    else if (m[3]) {
      const labelInner = m[3].match(/^([A-Z][A-Z\s]+?):\s*(.+)/);
      if (labelInner) {
        parts.push(
          <span key={m.index}>
            <span style={{ fontWeight: 700, color: T.text, fontSize: '0.85em', letterSpacing: '0.03em' }}>{labelInner[1]}:</span>{' '}
            <span style={{ fontWeight: 500, color: T.textMid }}>{labelInner[2]}</span>
          </span>
        );
      } else {
        parts.push(<strong key={m.index} style={{ fontWeight: 650, color: T.text }}>{m[3]}</strong>);
      }
    }
    else if (m[4]) parts.push(<em key={m.index} style={{ fontStyle: 'italic', color: T.textMid }}>{m[4]}</em>);
    else if (m[5]) parts.push(<span key={m.index} style={{ textDecoration: 'line-through', color: T.textMuted }}>{m[5]}</span>);
    else if (m[6]) parts.push(
      <code key={m.index} style={{
        background: 'rgba(212,146,30,0.08)', color: T.amber,
        padding: '2px 6px', borderRadius: 4, fontSize: '0.88em',
        fontFamily: "'SF Mono', Menlo, Monaco, monospace", fontWeight: 500,
        border: '1px solid rgba(212,146,30,0.12)',
      }}>{m[6]}</code>
    );
    else if (m[7]) parts.push(
      <a key={m.index} href={m[8]} target="_blank" rel="noopener noreferrer" style={{
        color: T.blue, textDecoration: 'none', fontWeight: 500,
        borderBottom: '1.5px solid rgba(74,158,204,0.3)',
      }}>{m[7]}</a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
};

export { parseInline, renderStatusTags };

const isCapsHeader = (line) => {
  const trimmed = line.trim();
  if (trimmed.length < 2 || trimmed.length > 50) return false;
  const mainPart = trimmed.split(/\s*[—–-]\s*/)[0];
  return /^[A-Z][A-Z\s\d&/,()]+$/.test(mainPart) && mainPart.length >= 2;
};

export const Md = ({ children, style }) => {
  const src = typeof children === 'string' ? children : String(children ?? '');
  if (!src) return <div style={{ fontSize: 14, color: T.textMid, ...style }} />;
  const lines = src.split('\n');
  const elements = [];
  let i = 0;
  let isFirst = true;
  let sectionCount = 0;

  const pushEl = (el) => { elements.push(el); isFirst = false; };

  if (lines.length > 0 && isCapsHeader(lines[0]) && /[—–-]/.test(lines[0])) {
    i = 1;
    if (i < lines.length && !lines[i].trim()) i++;
  }

  while (i < lines.length) {
    const line = lines[i];

    // Code block
    if (line.trimStart().startsWith('```')) {
      const lang = line.trim().slice(3).trim();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++;
      pushEl(
        <div key={elements.length} style={{ margin: '12px 0 16px', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)' }}>
          {lang && (
            <div style={{ background: 'rgba(0,0,0,0.06)', padding: '4px 14px', fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{lang}</div>
          )}
          <pre style={{
            background: 'rgba(0,0,0,0.03)', padding: '14px 16px', overflow: 'auto',
            fontSize: 13, lineHeight: 1.6, fontFamily: "'SF Mono', Menlo, Monaco, monospace",
            margin: 0, color: T.text,
          }}>
            <code>{codeLines.join('\n')}</code>
          </pre>
        </div>
      );
      continue;
    }

    // Markdown heading
    const hMatch = line.match(/^(#{1,4})\s+(.+)/);
    if (hMatch) {
      const level = hMatch[1].length;
      const headingStyles = {
        1: { fontSize: 17, fontWeight: 700, color: T.text, margin: isFirst ? '0 0 12px' : '24px 0 12px', letterSpacing: '-0.025em', lineHeight: 1.3, paddingBottom: 10, borderBottom: '2px solid rgba(0,0,0,0.06)' },
        2: { fontSize: 15, fontWeight: 650, color: T.text, margin: isFirst ? '0 0 10px' : '22px 0 10px', letterSpacing: '-0.02em', lineHeight: 1.35, paddingBottom: 8, borderBottom: '1px solid rgba(0,0,0,0.05)' },
        3: { fontSize: 14, fontWeight: 600, color: T.text, margin: isFirst ? '0 0 8px' : '18px 0 8px', lineHeight: 1.4 },
        4: { fontSize: 13, fontWeight: 600, color: T.amber, margin: isFirst ? '0 0 6px' : '14px 0 6px', lineHeight: 1.4, textTransform: 'uppercase', letterSpacing: '0.04em' },
      };
      pushEl(<div key={elements.length} style={headingStyles[level]}>{parseInline(hMatch[2])}</div>);
      i++;
      continue;
    }

    // ALL-CAPS section header
    if (isCapsHeader(line)) {
      const trimmed = line.trim();
      const mainPart = trimmed.split(/\s*[—–-]\s*/)[0].trim();
      const subPart = trimmed.includes('—') ? trimmed.split('—')[1]?.trim()
        : trimmed.includes('–') ? trimmed.split('–')[1]?.trim() : null;
      sectionCount++;
      pushEl(
        <div key={elements.length} style={{
          margin: isFirst ? '0 0 10px' : '20px 0 10px',
          paddingBottom: 8,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          display: 'flex', alignItems: 'baseline', gap: 8,
        }}>
          <span style={{
            fontSize: 11, fontWeight: 700, color: T.amber,
            letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>{mainPart}</span>
          {subPart && <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 400 }}>{subPart}</span>}
        </div>
      );
      i++;
      continue;
    }

    // Horizontal rule
    if (/^[-*_]{3,}\s*$/.test(line)) {
      pushEl(
        <div key={elements.length} style={{ margin: '20px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(0,0,0,0.08), rgba(0,0,0,0.03))' }} />
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(0,0,0,0.1)' }} />
          <div style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(0,0,0,0.03), rgba(0,0,0,0.08))' }} />
        </div>
      );
      i++;
      continue;
    }

    // Warning line
    if (line.trim().startsWith('⚠')) {
      pushEl(
        <div key={elements.length} style={{
          margin: '8px 0', padding: '10px 14px', borderRadius: 8,
          background: 'rgba(212,146,30,0.08)', border: '1px solid rgba(212,146,30,0.15)',
          display: 'flex', gap: 8, alignItems: 'flex-start',
          fontSize: 13, color: T.textMid, lineHeight: 1.6,
        }}>
          <span style={{ flexShrink: 0, fontSize: 14 }}>⚠️</span>
          <span>{parseInline(line.trim().replace(/^⚠\s*/, ''))}</span>
        </div>
      );
      i++;
      continue;
    }

    // Note line
    if (/^Note:/i.test(line.trim())) {
      pushEl(
        <div key={elements.length} style={{
          margin: '8px 0', padding: '10px 14px', borderRadius: 8,
          background: 'rgba(74,158,204,0.06)', border: '1px solid rgba(74,158,204,0.12)',
          fontSize: 12, color: T.textMuted, lineHeight: 1.6, fontStyle: 'italic',
        }}>
          {parseInline(line.trim())}
        </div>
      );
      i++;
      continue;
    }

    // Blockquote
    if (line.startsWith('>')) {
      const bqLines = [];
      while (i < lines.length && lines[i].startsWith('>')) {
        bqLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      pushEl(
        <blockquote key={elements.length} style={{
          margin: '12px 0', padding: '12px 16px',
          borderLeft: `3px solid ${T.amber}`, background: T.amberLight,
          borderRadius: '0 10px 10px 0', color: T.textMid, lineHeight: 1.65,
          fontSize: '0.95em', fontStyle: 'italic',
        }}>
          {parseInline(bqLines.join(' '))}
        </blockquote>
      );
      continue;
    }

    // Table
    if (/^\|(.+)\|/.test(line) && i + 1 < lines.length && /^\|[-:\s|]+\|/.test(lines[i + 1])) {
      const headers = line.split('|').filter(c => c.trim()).map(c => c.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && /^\|(.+)\|/.test(lines[i])) {
        rows.push(lines[i].split('|').filter(c => c.trim()).map(c => c.trim()));
        i++;
      }
      pushEl(
        <div key={elements.length} style={{ margin: '12px 0 16px', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.03)' }}>
                {headers.map((h, hi) => (
                  <th key={hi} style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: T.text, borderBottom: '2px solid rgba(0,0,0,0.08)' }}>{parseInline(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 1 ? 'rgba(0,0,0,0.015)' : 'transparent' }}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{ padding: '9px 14px', borderBottom: '1px solid rgba(0,0,0,0.04)', color: T.textMid, lineHeight: 1.5 }}>{parseInline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, ''));
        i++;
      }
      pushEl(
        <div key={elements.length} style={{ margin: '6px 0 12px', paddingLeft: 2 }}>
          {items.map((it, j) => (
            <div key={j} style={{ display: 'flex', gap: 10, marginBottom: 5, lineHeight: 1.65 }}>
              <span style={{ color: T.amber, fontWeight: 700, fontSize: 8, lineHeight: '1.65em', flexShrink: 0, marginTop: 5 }}>●</span>
              <span>{parseInline(it)}</span>
            </div>
          ))}
        </div>
      );
      continue;
    }

    // Ordered list
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
        items.push({ num: lines[i].match(/^\s*(\d+)/)[1], text: lines[i].replace(/^\s*\d+[.)]\s+/, '') });
        i++;
      }
      pushEl(
        <div key={elements.length} style={{ margin: '6px 0 12px' }}>
          {items.map((it, j) => (
            <div key={j} style={{
              display: 'flex', gap: 12, marginBottom: 8, lineHeight: 1.65,
              padding: '8px 12px', borderRadius: 8,
              background: j === 0 ? 'rgba(212,146,30,0.04)' : 'transparent',
              border: j === 0 ? '1px solid rgba(212,146,30,0.08)' : '1px solid transparent',
            }}>
              <span style={{
                color: j === 0 ? '#fff' : T.amber,
                fontWeight: 700, fontSize: 11, flexShrink: 0,
                width: 22, height: 22, borderRadius: '50%',
                background: j === 0 ? T.amber : T.amberLight,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginTop: 1,
              }}>{it.num}</span>
              <span style={{ flex: 1 }}>{parseInline(it.text)}</span>
            </div>
          ))}
        </div>
      );
      continue;
    }

    // Empty line
    if (!line.trim()) {
      i++;
      continue;
    }

    // Time entry
    const timeMatch = line.match(/^(\d{1,2}:\d{2})\s+(.+)/);
    if (timeMatch) {
      const entries = [];
      while (i < lines.length && /^\d{1,2}:\d{2}\s+/.test(lines[i])) {
        const tm = lines[i].match(/^(\d{1,2}:\d{2})\s+(.+)/);
        entries.push({ time: tm[1], desc: tm[2] });
        i++;
      }
      pushEl(
        <div key={elements.length} style={{ margin: '6px 0 12px' }}>
          {entries.map((e, j) => {
            const hasPREP = /PREP/.test(e.desc);
            const taggedDesc = renderStatusTags(e.desc) || parseInline(e.desc);
            return (
              <div key={j} style={{
                display: 'flex', gap: 12, padding: '7px 10px', marginBottom: 2,
                borderRadius: 6, alignItems: 'flex-start',
                background: hasPREP ? 'rgba(139,114,200,0.05)' : (j % 2 === 0 ? 'rgba(0,0,0,0.015)' : 'transparent'),
              }}>
                <span style={{
                  fontFamily: "'SF Mono', Menlo, Monaco, monospace",
                  fontSize: 12, fontWeight: 600, color: T.amber, flexShrink: 0,
                  minWidth: 42,
                }}>{e.time}</span>
                <span style={{ fontSize: 13, color: T.textMid, lineHeight: 1.5 }}>{taggedDesc}</span>
              </div>
            );
          })}
        </div>
      );
      continue;
    }

    // Label: value line
    const labelMatch = line.match(/^([A-Z][A-Za-z\s/&]{1,30}):\s+(.+)/);
    if (labelMatch && !line.startsWith('http')) {
      const tagged = renderStatusTags(labelMatch[2]);
      pushEl(
        <p key={elements.length} style={{ margin: '0 0 6px', lineHeight: 1.7 }}>
          <span style={{ fontWeight: 600, color: T.text }}>{labelMatch[1]}:</span>{' '}
          {tagged || parseInline(labelMatch[2])}
        </p>
      );
      i++;
      continue;
    }

    // Contact line
    const contactMatch = line.match(/^(.+?)\s*[—–]\s+(.+)/);
    if (contactMatch && !isCapsHeader(line) && elements.length > 0) {
      pushEl(
        <div key={elements.length} style={{
          margin: '4px 0', padding: '6px 10px', borderRadius: 6,
          borderLeft: `2px solid rgba(212,146,30,0.2)`,
          lineHeight: 1.6,
        }}>
          <span style={{ fontWeight: 600, color: T.text, fontSize: 13 }}>{parseInline(contactMatch[1])}</span>
          <span style={{ color: T.textMuted }}> — </span>
          <span style={{ color: T.textMid, fontSize: 13 }}>{parseInline(contactMatch[2])}</span>
        </div>
      );
      i++;
      continue;
    }

    // Paragraph
    const pLines = [];
    while (i < lines.length && lines[i].trim() &&
      !isCapsHeader(lines[i]) &&
      !/^(#{1,4}\s|[-*_]{3,}\s*$|>\s|```|\s*[-*+]\s+|\s*\d+[.)]\s+|\|.+\||⚠|\d{1,2}:\d{2}\s)/.test(lines[i]) &&
      !/^Note:/i.test(lines[i].trim())) {
      pLines.push(lines[i]);
      i++;
    }
    if (pLines.length > 0) {
      const joined = pLines.join(' ');
      const tagged = renderStatusTags(joined);
      pushEl(
        <p key={elements.length} style={{ margin: '0 0 10px', lineHeight: 1.75, letterSpacing: '0.005em' }}>
          {tagged || parseInline(joined)}
        </p>
      );
    }
  }

  return <div style={{ fontSize: 14, color: T.textMid, ...style }}>{elements}</div>;
};
